'use client';

import type { PropsWithChildren} from 'react';
import { Fragment, useCallback, useRef } from 'react';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';

import { useReferralUrlQuery } from '@query/user';

import type { ActivityHandle } from '@ui/uikit/components/common/Activity';
import { Activity } from '@ui/uikit/components/common/Activity';
import { Button } from '@ui/uikit/components/common/Button';
import { useDropdownMenu } from '@ui/uikit/components/common/DropdownMenu';
import type {
  MessageMapping} from '@ui/uikit/components/common/SharingDropdownMenu';
import {
  SharingDropdownMenuController,
} from '@ui/uikit/components/common/SharingDropdownMenu';
import { Share } from '@ui/uikit/components/icons/mono/Share';

interface ApplicationSharingControllerProps {
  applicationId: string;
}

const MESSAGES_BY_PLATFORM: MessageMapping = {
  X: "Look what I've found on the #MagicStore, the World&apos;s #Web3 App Store!🤩 Check the #dApp out here: {{url}}",
  default:
    "Look what I've found on the Magic Store!🤩 This app is a gem 💎 Check it out here: {{url}}",
};

const FALLBACK_SHARING_URL = typeof window != 'undefined' ? window.document.URL : undefined;

const ApplicationSharingController = ({
  applicationId,
  children,
}: PropsWithChildren<ApplicationSharingControllerProps>) => {
  const { isAuth } = useAuth();

  const { data: referralUrl } = useReferralUrlQuery(applicationId, 'app', {
    enabled: isAuth,
  });

  return (
    <SharingDropdownMenuController
      url={referralUrl?.sharingLink || FALLBACK_SHARING_URL}
      platforms={['X', 'Discord', 'Telegram', 'Facebook', 'Reddit', 'LinkedIn', 'Email']}
      title="Share app with friends"
      messageTemplate={MESSAGES_BY_PLATFORM}
    >
      {children}
    </SharingDropdownMenuController>
  );
};

interface ApplicationSharingControlProps {
  applicationName?: string;
}

const ApplicationSharingControl = ({ applicationName }: ApplicationSharingControlProps) => {
  const { openMenu } = useDropdownMenu();

  const { user } = useAuth();

  const { open: openAuthModal } = useAuthModal();

  const button = useRef<HTMLButtonElement & HTMLAnchorElement>(null);

  const handleClick = useCallback(async () => {
    if (!flow.current) {
      console.warn(`Flow handle not initialized.`);

      return;
    }

    if (!button.current) {
      console.warn(`Button handle not initialized.`);

      return;
    }

    await flow.current.initiate();

    openMenu(button.current);
  }, [openMenu]);

  const flow = useRef<ActivityHandle>(null);

  const signIn = useCallback(() => {
    openAuthModal();
  }, [openAuthModal]);

  return (
    <Fragment>
      <Activity ref={flow}>
        <Activity.Step requirement={!!user} action={signIn} key="signin" />
      </Activity>

      <Button
        variant="secondary"
        size="large"
        onClick={handleClick}
        ref={button}
        label={ `Share ${applicationName ?? 'application' } with your friends on social media` }
      >
        <Share />
      </Button>
    </Fragment>
  );
};

export { ApplicationSharingControl, ApplicationSharingController };
